<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t, locale } = useI18n();

const store = useStore();

const confirmDeleteContactDialogRef = ref(null);
const contactLabelsRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});


const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt, locale.value) 
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt, locale.value)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'), { type: 'success' });
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'), { type: 'error' });
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'), { type: 'success' });
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'), { type: 'error' });
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'), { type: 'success' });
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE'),
      { type: 'error' }
    );
  }
};

// Expose label data to parent
const labelMenuItems = computed(() => {
  return contactLabelsRef.value?.labelMenuItems || [];
});

const handleLabelAction = (event) => {
  if (contactLabelsRef.value) {
    contactLabelsRef.value.handleLabelAction(event);
  }
};

defineExpose({
  labelMenuItems,
  handleLabelAction,
});
</script>

<template>
  <div class="flex flex-col items-start gap-6">
    <div class="flex flex-col gap-3 flex-row justify-between sm:gap-4 border-b border-n-gray-2 dark:border-n-gray-8 w-full">
      <div class="flex items-start gap-3">
        <Avatar :roundedFull="true" :src="avatarSrc || ''" :name="selectedContact?.name || ''" :size="50" allow-upload
          @upload="handleAvatarUpload" @delete="handleAvatarDelete" />
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-gray-7 dark:text-n-gray-3">
            {{ selectedContact?.name }}
          </h3>

          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 ">
            <span class="inline-flex items-center gap-1 text-xs font-medium text-n-gray-7 dark:text-n-gray-3"
              v-if="selectedContact?.identifier">
              <i class="i-lucide-user-plus size-[1.125rem]" />
              {{ selectedContact?.identifier }}
            </span>

            <span class="inline-flex items-center gap-1 text-xs text-n-gray-6">
              <i class="i-lucide-activity size-[0.875rem]" />
              {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
              •
              {{
                $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                  date: lastActivityAt,
                })
              }}
            </span>
          </div>
        </div>
      </div>

      <ContactLabels ref="contactLabelsRef" class="flex-shrink-0" :contact-id="selectedContact?.id" />
    </div>
    <div class="flex flex-col items-start gap-6 w-full">
      <ContactsForm ref="contactsFormRef" :contact-data="contactData" is-details-view show-social
        visible-fields="['FIRST_NAME', 'EMAIL_ADDRESS', 'PHONE_NUMBER', 'CITY', 'COUNTRY']"
        @update="handleFormUpdate" />
    </div>
    <div class="flex justify-between md:justify-center items-center w-full gap-4 border-n-strong">
      <button
        class="flex-1 md:flex-none h-10 px-4 rounded-lg border outline-1 outline-n-strong text-n-gray-5 bg-white dark:bg-[#23282F] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
        @click="openConfirmDeleteContactDialog">
        {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
      </button>

      <button class="flex-1 md:flex-none h-10 px-4 rounded-lg bg-n-bluegray-4 text-white hover:opacity-80 disabled:opacity-50"
        :disabled="isUpdating || isFormInvalid" @click="updateContact">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON') }}
      </button>
    </div>
    <ConfirmContactDeleteDialog ref="confirmDeleteContactDialogRef" :selected-contact="selectedContact"
      @go-to-contacts-list="emit('goToContactsList')" />
  </div>
</template>